// P1-8: the fourth running count the requirements ask for -- Duplicate (spec §2.1).
//
// Deferred from step 3 of the execution order on purpose: a duplicate count is only worth showing
// once P1-3 makes detection deterministic, and until that branch it wasn't. See
// utilities/scanMatch.js.
//
// ── WHY A SESSION TALLY, AND NOT SERVER-SIDE ────────────────────────────────
// The other four tiles come from one server-side definition (P1-2, CC_Backend/utils/stats.js)
// because they describe rows that exist. A duplicate is a scan that was REFUSED -- nothing is
// persisted, so there is nothing for the server to count. Remediation Plan v2 §3 defines it as "a
// running tally of rejected duplicate scan attempts (session counter)", and spec §4.2/§4.3 do not
// list it among the report counts.
//
// So it deliberately does NOT go into the `stats` object: putting a client-only key in a
// server-authoritative shape would imply it is reportable, and someone would then put it in the XLS.
//
// ── P1-18: THE COUNT NOW CLEARS WHEN ITS CAUSE DOES ─────────────────────────
// CompuCycle reported: scanned an already-collected item (Duplicate -> 1), deleted the offending
// item, and the tile stayed at 1. That was faithful to the definition above -- a refused scan
// happened, and deleting a row later does not un-happen it -- but the definition was the problem.
// The tile sits beside three counts that all describe the STATE of the order, so "Duplicate: 1"
// reads as "there is one duplicate here", not "a scan was refused earlier", and a number that will
// not go down is indistinguishable from a stuck one.
//
// Each event records the row it collided with, so the count is now DERIVED: an event counts only
// while its cause still holds -- the row exists, is not soft-deleted, and is still collected. Delete
// the item and the count drops; un-collect it and the count drops, which is also right, because at
// that point a re-scan would succeed. The requirement is still met: the refused scan is counted at
// the moment it happens.
//
// ── WHY THE EVENT LIST IS NO LONGER CAPPED ──────────────────────────────────
// It used to keep the most recent 50. Now that the count is derived from the events, a cap would
// silently make the count wrong past 50 -- so retention is unbounded and the DIALOG caps what it
// renders instead. An event is a handful of short strings and the tally resets per order, so the
// memory is irrelevant next to a count that lies.

const EMPTY_TALLY = {
    collectionId: null,
    events: []
};

// How many of the newest events the log dialog shows. Display only -- never retention.
const MAX_DISPLAYED_EVENTS = 50;

const forCollection = (collectionId) => ({
    collectionId: collectionId ?? null,
    events: []
});

// Same loose comparison as scanMatch.assetsInOrder: ids arrive as a number from redux and a string
// from the route params.
const sameCollection = (tally, collectionId) =>
    tally != null && String(tally.collectionId) === String(collectionId);

/**
 * Record one rejected duplicate scan. Pure -- returns a new tally.
 *
 * A tally belonging to a different order is replaced rather than added to, so the count always
 * describes the order on screen (spec §5.2).
 */
const recordDuplicate = (tally, event = {}) => {
    const {
        collectionId, serial = '', assetTag = '', assetId = null, at = null,
        method = null, trigger = null, evidence = null
    } = event;

    const current = sameCollection(tally, collectionId) ? tally : forCollection(collectionId);

    return {
        collectionId: collectionId ?? null,
        events: [
            { serial, assetTag, assetId, at, method, trigger, evidence },
            ...current.events
        ]
    };
};

/**
 * Does this event's reason for existing still hold?
 *
 * An event with no `assetId` cannot be validated against anything -- the server's DUPLICATE_SCAN
 * response may not name a row -- so it counts unconditionally rather than silently vanishing.
 */
const isStillDuplicate = (event, assetsById) => {
    if (event == null) return false;
    if (event.assetId == null) return true;

    const row = assetsById.get(String(event.assetId));
    return row != null && row.deleted !== true && row.collected === true;
};

const indexAssets = (assets) => new Map(
    (Array.isArray(assets) ? assets : [])
        .filter(a => a != null && a.id != null)
        .map(a => [String(a.id), a]));

/**
 * The refusals that still stand, newest first.
 *
 * `assets` is the loaded rows for the order. Passing nothing means "cannot verify", and every
 * recorded event is returned -- the pre-P1-18 behaviour, which is the safe direction: it would
 * rather show a stale count than hide a real refusal.
 */
const liveDuplicateEvents = (tally, collectionId, assets = null) => {
    if (!sameCollection(tally, collectionId)) {
        return [];
    }
    if (assets == null) {
        return tally.events;
    }
    const assetsById = indexAssets(assets);
    return tally.events.filter(event => isStillDuplicate(event, assetsById));
};

const duplicateCount = (tally, collectionId, assets = null) =>
    liveDuplicateEvents(tally, collectionId, assets).length;

// Newest first, capped for rendering only.
const duplicateEvents = (tally, collectionId, assets = null) =>
    liveDuplicateEvents(tally, collectionId, assets).slice(0, MAX_DISPLAYED_EVENTS);

// How many are being withheld from the rendered list, so the dialog can say so.
const hiddenDuplicateCount = (tally, collectionId, assets = null) =>
    Math.max(0, duplicateCount(tally, collectionId, assets) - MAX_DISPLAYED_EVENTS);

/**
 * Is there still a standing refusal against THIS row?
 *
 * Added 2026-08-21 for the pink row highlight, which had the same defect the count had before
 * P1-18: it was set when a duplicate was refused and then only cleared by the next ACCEPTED
 * entry, so pressing Clear on the duplicate log zeroed the count while the row stayed pink until
 * someone scanned something else. CompuCycle reported exactly that.
 *
 * Same fix, same reasoning: derive it rather than tracking a separate "cleared" flag. The
 * highlight means "this is the row you just tried to scan again", so it should last exactly as
 * long as that statement is true -- which is what liveDuplicateEvents already computes. Clearing
 * the tally, deleting the row, or un-collecting it all end it, because after any of the three a
 * re-scan would succeed.
 *
 * Ids are compared as strings: this one arrives from the redux row as a number and from the
 * server's duplicateOfId as either, and a type mismatch would silently mean no highlight.
 */
const hasLiveDuplicateFor = (tally, collectionId, assets = null, assetId = null) =>
    assetId != null && liveDuplicateEvents(tally, collectionId, assets)
        .some(event => event.assetId != null && String(event.assetId) === String(assetId));

/**
 * P1-18: an explicit reset, so the operator is never stuck with a number they can only clear by
 * reloading the page. Scoped to the order on screen.
 */
const clearDuplicates = (tally, collectionId) =>
    (sameCollection(tally, collectionId) ? forCollection(collectionId) : (tally ?? EMPTY_TALLY));

const exports = {
    EMPTY_TALLY,
    MAX_DISPLAYED_EVENTS,
    recordDuplicate,
    duplicateCount,
    duplicateEvents,
    hiddenDuplicateCount,
    hasLiveDuplicateFor,
    clearDuplicates,
    isStillDuplicate,
};

export default exports;

export {
    EMPTY_TALLY,
    MAX_DISPLAYED_EVENTS,
    recordDuplicate,
    duplicateCount,
    duplicateEvents,
    hiddenDuplicateCount,
    hasLiveDuplicateFor,
    clearDuplicates,
    isStillDuplicate,
};
